/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './FullScreenToggle';

const FullScreenToggleManager: ComponentManagerFn<'FullScreenToggle'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'FullScreenToggle'>(platform, 'FullScreenToggle'),
    type: 'FullScreenToggle',
    name: 'Full Screen Toggle',
    defaultValue,
    props: {},
    category: 'Forms',
    icon: { iconName: 'expand-alt' },
    description: `**Overview**<br/>
The Full Screen Toggle component is a utility control that renders a clickable icon button. Its sole function is to toggle the application interface between standard windowed mode and the browser's native full-screen mode (hiding the URL bar, tabs, and browser frame).
Use this component specifically when designing **Custom Headers** or specialized dashboards where you have disabled the standard application navigation bar but still want to offer users the ability to maximize their workspace.

**1. Zero Configuration**<br/>
This is a "plug-and-play" component. It requires no complex configuration, data binding, or property settings to function.
* **Behavior:** It automatically detects the current state of the browser. If the app is windowed, clicking it expands to full screen. If already full screen, clicking it reverts to windowed mode.

**2. Integration Context**<br/>
The standard application header already includes a full-screen toggle button by default.
* **When to use:** Only use this component if you have hidden the default application header and are building a custom top-bar or navigation menu from scratch.
* **Redundancy:** Adding this to a page that already retains the standard header will result in two identical toggle buttons on the screen.

**3. Best Use Cases**<br/>
* **Custom Headers:** Replicating standard navigation functionality in a branded, custom-built header.
* **Executive Dashboards:** Allowing users to expand a BI dashboard to fill a large monitor or TV screen for presentations.
* **Kiosk Interfaces:** Providing a simple way to maximize the application for public-facing or manufacturing floor terminals.
* **Complex Data Grids:** Giving power users an easy way to maximize screen real estate when working with wide spreadsheets or detailed maps.`,
  };
};

export default FullScreenToggleManager;
